/**
 * CAS (QQI FET / Component Award Scheme) descriptor mapper.
 *
 * Maps a {@link Programme} to the descriptor data for a QQI FET Common Award
 * Scheme (CAS) programme. It consumes the shared descriptor core
 * ({@link buildDescriptorCore}) for the version-agnostic field mapping and shared
 * services, then applies the CAS-specific overrides: FET award-class terminology,
 * the QQI CAS Major Award code, and FET credit terminology (distinct from the
 * non-CAS ECTS wording).
 *
 * @module export/cas-descriptor-data
 */

import type { Module, Programme, ProgrammeVersion } from "@/types";
import { normalizeDeliveryModality, resolveEffortHours } from "@/utils/helpers";
import { versionDurationYears } from "@/utils/programme-duration";
import { versionTeachingWeeks } from "@/utils/teaching-weeks";

import {
  buildDescriptorCore,
  type DescriptorAppendixStrand,
  type DescriptorAppendixStrands,
  type DescriptorBuildContext,
  type DescriptorData,
  type DescriptorModule,
  programmeIntakeMonths,
} from "./descriptor-core";
import { representativeStageModules } from "./schedule-data";

/** One row of the CAS award-standards overview analysis (a QQI sub-strand group). */
export interface CasAppendixCells {
  /** Knowledge/skill/competence statement text at the programme's NFQ level. */
  standard: string;
  /** MIPLO codes addressing the sub-strand. */
  miplos: string;
  /** Per-MIPLO evidence summary (module and MIMLO references). */
  evidence: string;
}

/** Per-version programme duration breakdown (years, months, weeks, days). */
export interface CasVersionDuration {
  /** Version label (e.g. "Full-time"). */
  label: string;
  /** Calendar years, when derivable from the version's duration text. */
  years: string;
  /** Calendar months; blank when not derivable. */
  months: string;
  /** Duration in weeks, from the version's authored week count. */
  weeks: string;
  /** Calendar days; blank when not derivable. */
  days: string;
}

/** Per-version attendance option with derived weekly contact hours. */
export interface CasAttendanceOption {
  versionLabel: string;
  /**
   * Learner direct contact hours per week (number plus description), derived
   * from the version's module contact hours over its teaching weeks; blank
   * when the version has no contact-hour or teaching-week data.
   */
  contactHoursPerWeek: string;
}

/** Per-version intake planning row. */
export interface CasVersionIntake {
  versionLabel: string;
  /** Planned first intake: the first of the version's intakes. */
  firstIntake: string;
  /** Intakes per annum, from the version's authored intake list. */
  intakesPerAnnum: string;
  /** Minimum enrolment for viability; not modelled, always blank. */
  enrolmentMinimum: string;
  /** Maximum enrolment that can be accommodated (target cohort size). */
  enrolmentMaximum: string;
}

/**
 * Descriptor data for a CAS (QQI FET) programme. Extends the canonical
 * {@link DescriptorData} with the CAS-only fields the non-CAS mapper omits.
 */
export interface CasDescriptorData extends DescriptorData {
  /** QQI CAS Major Award code for the programme (e.g. "5M0529"). */
  casMajorAwardCode: string;
  /** FET credit value rendered with FET terminology (e.g. "120 FET credits"). */
  fet_credits: string;
  /**
   * Plain FET credit total (e.g. "120") for cells whose label already reads
   * "FET Credits", where the labelled {@link fet_credits} form would repeat the
   * unit.
   */
  fe_credits_total: string;
  /**
   * Maximum FET credits in any single stage (academic year). The same stage
   * credit-target derivation as the core's `maximum_annual_ects`, exposed under
   * FET terminology so the CAS template never references an ECTS-named field.
   */
  fe_credits_max_annual: string;
  /** Title of the QQI CAS Major Award (e.g. from the CAS compliance record). */
  cas_major_award_title: string;
  /**
   * The linked (major) certificate the programme is expected to lead to, as
   * "<code> <title>".
   */
  expected_award_linked: string;
  /**
   * The CAS component certificates achievable on the programme, as a
   * comma-separated list of component codes.
   */
  expected_award_components: string;
  /** Programme duration rows, one per programme version. */
  versionDurations: CasVersionDuration[];
  /** Attendance options, one per programme version. */
  attendanceOptions: CasAttendanceOption[];
  /** Intake planning rows, one per programme version. */
  versionIntakes: CasVersionIntake[];
  /**
   * Intake spacing narrative; populated from the programme's intake months
   * only when a version runs more than one intake per annum.
   */
  intake_spacing: string;
  /**
   * Rationale for the choice of elective minor awards: the elective-choice
   * summary when the programme defines electives, otherwise a derived
   * statement that every component is mandatory.
   */
  elective_rationale: string;
  /** Knowledge sub-strand analysis row (breadth and kind). */
  cas_appendix_knowledge: CasAppendixCells;
  /** Know-how and skill sub-strand analysis row (range and selectivity). */
  cas_appendix_knowhow: CasAppendixCells;
  /** Combined competence sub-strand analysis row (context, role, learning to learn, insight). */
  cas_appendix_competence: CasAppendixCells;
}

/**
 * Maps an FE award type to its FET award-class label by promoting the "FE"
 * prefix to "FET" (e.g. "FE Major Award" → "FET Major Award"). Award types that
 * do not carry the FE prefix are returned trimmed and unchanged.
 *
 * @param awardType - The programme's award type
 * @returns The FET award-class label
 */
function toFetAwardClass(awardType: string): string {
  const trimmed = (awardType ?? "").trim();
  if (trimmed.startsWith("FE ")) {
    return `FET ${trimmed.slice("FE ".length)}`;
  }
  return trimmed;
}

function durationYearsOf(version: ProgrammeVersion): string {
  const years = versionDurationYears(version);
  return years === undefined ? "" : String(years);
}

function buildVersionDurations(versions: ProgrammeVersion[]): CasVersionDuration[] {
  return versions.map((version) => ({
    label: version.label ?? version.code ?? "",
    years: durationYearsOf(version),
    months: "",
    weeks: String(versionTeachingWeeks(version) ?? ""),
    days: "",
  }));
}

/**
 * Derive each version's learner direct-contact hours per week: the sum of
 * classroom, mentoring, and other contact hours across one learner's modules
 * (one option per elective slot, as the schedule counts), divided by its
 * teaching weeks.
 */
function buildAttendanceOptions(p: Programme): CasAttendanceOption[] {
  const modules = p.modules ?? [];
  return (p.versions ?? []).map((version) => {
    const deliveryKey = `${version.id}_${version.deliveryModality}`;
    const stages = version.stages ?? [];
    const moduleIds = stages.flatMap((stage) =>
      representativeStageModules(p, stage).map((assignment) => assignment.moduleId),
    );
    const contactHours = moduleIds.reduce((total, moduleId) => {
      const mod = modules.find((candidate) => candidate.id === moduleId);
      if (!mod) {
        return total;
      }
      const effort = resolveEffortHours(mod, deliveryKey);
      return (
        total +
        Number(effort.classroomHours ?? 0) +
        Number(effort.mentoringHours ?? 0) +
        Number(effort.otherContactHours ?? 0)
      );
    }, 0);
    const teachingWeeks = stages.reduce((total, stage) => total + (stage.teachingWeeks ?? 0), 0);
    const perWeek = teachingWeeks > 0 ? contactHours / teachingWeeks : 0;
    const rounded = Math.round(perWeek * 10) / 10;
    return {
      versionLabel: version.label ?? version.code ?? "",
      contactHoursPerWeek:
        rounded > 0
          ? `${rounded} hours direct contact per week across ${teachingWeeks} teaching weeks`
          : "",
    };
  });
}

function buildVersionIntakes(p: Programme): CasVersionIntake[] {
  return (p.versions ?? []).map((version) => {
    const intakes = version.intakes ?? [];
    return {
      versionLabel: version.label ?? version.code ?? "",
      firstIntake: intakes[0] ?? "",
      intakesPerAnnum: intakes.length > 0 ? String(intakes.length) : "",
      enrolmentMinimum: "",
      enrolmentMaximum: version.targetCohortSize != null ? String(version.targetCohortSize) : "",
    };
  });
}

/** Format an hour count for a schedule cell, leaving zero blank. */
function casHours(value: number): string {
  return value > 0 ? String(value) : "";
}

/** Find the programme version whose stages carry the given module. */
function owningVersion(
  moduleId: string,
  versions: ProgrammeVersion[],
): ProgrammeVersion | undefined {
  return versions.find((version) =>
    (version.stages ?? []).some((stage) =>
      (stage.modules ?? (stage.moduleIds ?? []).map((id) => ({ moduleId: id }))).some(
        (assignment) => assignment.moduleId === moduleId,
      ),
    ),
  );
}

/**
 * Re-project a module's descriptor view with CAS semantics.
 *
 * The FET module-overview effort analysis groups direct contact by the owning
 * version's delivery modality (face-to-face delivery counts classroom,
 * mentoring, and other contact as in-person; online delivery counts them as
 * synchronous online; blended keeps classroom in person and the rest
 * synchronous), and its indirect categories have no independent-learning row,
 * so independent hours render under "Other (Identify)" with a description.
 * Each learning outcome also carries its CAS component code and the matching
 * component learning-outcome reference, and the module lists the CAS component
 * award achievable on it.
 */
function projectCasModule(
  descriptorModule: DescriptorModule,
  mod: Module,
  p: Programme,
  context: DescriptorBuildContext,
): DescriptorModule {
  const version = owningVersion(mod.id, p.versions ?? []);
  const deliveryKey = `${version?.id}_${version?.deliveryModality}`;
  const effort = resolveEffortHours(mod, deliveryKey);
  const classroom = Number(effort.classroomHours ?? 0);
  const mentoring = Number(effort.mentoringHours ?? 0);
  const otherContact = Number(effort.otherContactHours ?? 0);
  const directed = Number(effort.directedElearningHours ?? 0);
  const independent = Number(effort.independentLearningHours ?? 0);
  const workBased = Number(effort.workBasedHours ?? 0);
  const other = Number(effort.otherHours ?? 0);

  const contact = classroom + mentoring + otherContact;
  const modality = normalizeDeliveryModality(version?.deliveryModality ?? "");
  let onsite = 0;
  let syncOnline = 0;
  if (modality === "ONLINE") {
    syncOnline = contact;
  } else if (modality === "BLENDED") {
    onsite = classroom;
    syncOnline = mentoring + otherContact;
  } else {
    onsite = contact;
  }
  const otherTotal = independent + other;
  const otherDescription = [
    independent > 0 ? "Independent learning" : "",
    descriptorModule.effortOtherDescription,
  ]
    .filter(Boolean)
    .join("; ");
  const total = contact + directed + independent + workBased + other;

  const casComponentCode = mod.casComponentCode ?? "";
  const componentTitle =
    context.casAwards?.components.find((component) => component.code === casComponentCode)?.title ??
    (p.embeddedAwards ?? []).find((award) => award.casComponentCode === casComponentCode)?.title ??
    mod.title ??
    "";
  const casOriginals = mod.casOriginalMimlos ?? [];

  return {
    ...descriptorModule,
    effortOnsite: casHours(onsite),
    effortSyncOnline: casHours(syncOnline),
    effortSyncHybrid: "",
    effortAsync: casHours(directed),
    effortIndependent: casHours(independent),
    effortWorkBased: casHours(workBased),
    effortOther: casHours(otherTotal),
    effortOtherDescription: otherTotal > 0 ? otherDescription : "",
    effortTotal: casHours(total),
    mimlos: descriptorModule.mimlos.map((mimlo, index) => {
      const sourceMimlo = (mod.mimlos ?? [])[index];
      const loIndex = sourceMimlo
        ? casOriginals.findIndex(
            (original) => original.id === sourceMimlo.id || original.text === sourceMimlo.text,
          )
        : -1;
      return {
        ...mimlo,
        componentCode: casComponentCode,
        componentLo: loIndex >= 0 ? `LO ${loIndex + 1}` : "",
      };
    }),
    casComponents: casComponentCode ? [{ code: casComponentCode, title: componentTitle }] : [],
  };
}

function buildCasModules(
  coreModules: DescriptorModule[],
  p: Programme,
  context: DescriptorBuildContext,
): DescriptorModule[] {
  return coreModules.map((descriptorModule) => {
    const mod = (p.modules ?? []).find((candidate) =>
      descriptorModule.code
        ? candidate.code === descriptorModule.code
        : candidate.title === descriptorModule.title,
    );
    return mod ? projectCasModule(descriptorModule, mod, p, context) : descriptorModule;
  });
}

/**
 * Collapse one or more appendix strands into the cells of one overview-analysis
 * row: the NFQ-level statement text, the MIPLO codes addressing the sub-strand,
 * and a per-MIPLO module/MIMLO evidence summary.
 */
export function buildCasAppendixCells(strands: DescriptorAppendixStrand[]): CasAppendixCells {
  const standard = strands
    .flatMap((strand) => strand.levelNDescriptors.map((descriptor) => descriptor.text))
    .filter(Boolean)
    .join("\n");
  const relationships = strands.flatMap((strand) => strand.relationships);
  const miplos = [...new Set(relationships.map((rel) => rel.miploCode).filter(Boolean))].join(", ");
  const evidence = relationships
    .map((rel) => {
      const modules = rel.evidence
        .map((entry) => `${entry.moduleCode} ${entry.mimloCode}`.trim())
        .filter(Boolean)
        .join(", ");
      return modules ? `${rel.miploCode}: ${modules}` : rel.miploCode;
    })
    .filter(Boolean)
    .join("\n");
  return { standard, miplos, evidence };
}

function buildCasAppendixRows(strands: DescriptorAppendixStrands): {
  knowledge: CasAppendixCells;
  knowhow: CasAppendixCells;
  competence: CasAppendixCells;
} {
  return {
    knowledge: buildCasAppendixCells([strands.knowledgeBreadth]),
    knowhow: buildCasAppendixCells([strands.knowhowSkill]),
    competence: buildCasAppendixCells([
      strands.competenceContext,
      strands.competenceRole,
      strands.competenceLearning,
      strands.competenceInsight,
    ]),
  };
}

/**
 * Build the descriptor data for a CAS (QQI FET) programme.
 *
 * @param p - The full programme state
 * @returns CasDescriptorData ready for docx-templates createReport()
 */
export function buildCasDescriptorData(
  p: Programme,
  context: DescriptorBuildContext = {},
): CasDescriptorData {
  const core = buildDescriptorCore(p, context);
  // A CAS programme's credit values are FET credits; totalCredits carries the
  // same FET value when the dedicated feCredits field is unset.
  const feCredits = p.feCredits ?? p.totalCredits;
  const casMajorAwardCode = p.casMajorAwardCode ?? "";
  // The authoritative title comes from the CAS awards dataset when the caller
  // supplies it; programmes imported with a casCompliance record carry the
  // title inline (not a first-class Programme field, hence the cast).
  const casCompliance = p.casCompliance as { majorAwardTitle?: string } | undefined;
  const casMajorAwardTitle =
    context.casAwards?.major_awards.find((award) => award.code === casMajorAwardCode)?.title ??
    casCompliance?.majorAwardTitle ??
    "";
  const appendixRows = buildCasAppendixRows(core.appendixStrands);
  return {
    ...core,
    modules: buildCasModules(core.modules, p, context),
    award_class: toFetAwardClass(p.awardType ?? ""),
    casMajorAwardCode,
    fet_credits: feCredits != null ? `${feCredits} FET credits` : "",
    fe_credits_total: feCredits != null ? String(feCredits) : "",
    fe_credits_max_annual: core.maximum_annual_ects,
    cas_major_award_title: casMajorAwardTitle,
    expected_award_linked: `${casMajorAwardCode} ${casMajorAwardTitle}`.trim(),
    expected_award_components: core.casComponentAwards
      .map((award) => award.casComponentCode || award.title)
      .filter(Boolean)
      .join(", "),
    versionDurations: buildVersionDurations(p.versions ?? []),
    attendanceOptions: buildAttendanceOptions(p),
    versionIntakes: buildVersionIntakes(p),
    intake_spacing: (p.versions ?? []).some((version) => (version.intakes ?? []).length > 1)
      ? programmeIntakeMonths(p).join(", ")
      : "",
    elective_rationale:
      core.elective_choice_summary ||
      ((p.electiveDefinitions ?? []).length === 0 &&
      (p.modules ?? []).every((mod) => !mod.isElective)
        ? "All CAS minor awards on this programme are mandatory; no elective choices are offered."
        : ""),
    cas_appendix_knowledge: appendixRows.knowledge,
    cas_appendix_knowhow: appendixRows.knowhow,
    cas_appendix_competence: appendixRows.competence,
  };
}
